import { Task } from '../types';

/**
 * Canonical normalization for task types across German and English legacy/current labels.
 * 100% behavior-compatible with useGlobalKanbanColumns and single-project views.
 */
export function normalizeTaskType(rawType?: string): string {
  const lower = (rawType || 'task').toLowerCase().trim();
  switch (lower) {
    case 'aufgabe':
    case 'task':
      return 'task';
    case 'meilenstein':
    case 'milestone':
      return 'milestone';
    case 'deadline':
      return 'deadline';
    case 'meeting':
      return 'meeting';
    case 'entscheidung':
    case 'decision':
      return 'decision';
    case 'freigabe':
    case 'approval':
      return 'approval';
    case 'workshop':
      return 'workshop';
    case 'iteration':
      return 'iteration';
    case 'review':
      return 'review';
    case 'kooperation':
    case 'cooperation':
      return 'cooperation';
    case 'kontakt':
    case 'contact':
      return 'contact';
    default:
      return lower;
  }
}

/**
 * Canonical normalization for task status values across German and English labels.
 * Note: 'pending' / 'in arbeit' / 'contacted' map to 'in_progress' for normalized filter comparisons,
 * while Kanban status columns map active tasks to 'open'/'todo', 'hold', or 'completed'.
 */
export function normalizeTaskStatus(rawStatus?: string): string {
  const lower = (rawStatus || 'open').toLowerCase().trim();
  if (lower === 'offen' || lower === 'vormerkung' || lower === 'todo' || lower === 'open') {
    return 'open';
  }
  if (
    lower === 'in arbeit' ||
    lower === 'in_progress' ||
    lower === 'pending' ||
    lower === 'in kontakt' ||
    lower === 'contacted' ||
    lower === 'warte auf antwort'
  ) {
    return 'in_progress';
  }
  if (lower === 'wartend' || lower === 'hold' || lower === 'waiting') {
    return 'hold';
  }
  if (lower === 'blockiert' || lower === 'blocked') {
    return 'blocked';
  }
  if (lower === 'erledigt' || lower === 'done' || lower === 'partner' || lower === 'completed') {
    return 'completed';
  }
  return lower;
}

/**
 * Checks whether a task is considered completed by either boolean flag or status string.
 */
export function isTaskCompleted(task: Pick<Task, 'completed' | 'status'>): boolean {
  if (task.completed) return true;
  const s = (task.status || '').toLowerCase().trim();
  return s === 'completed' || s === 'done' || s === 'erledigt' || s === 'partner';
}

/**
 * Checks whether a task is on hold / waiting / blocked.
 */
export function isTaskOnHold(task: Pick<Task, 'status'>): boolean {
  const s = (task.status || '').toLowerCase().trim();
  return s === 'hold' || s === 'waiting' || s === 'wartend' || s === 'blocked' || s === 'blockiert';
}

/**
 * Checks whether a task has an active running timer or positive tracked time.
 */
export function hasTaskTimerActivity(task: Pick<Task, 'timerStartedAt' | 'trackedTime'>): boolean {
  const hasRunningTimer = Boolean(task.timerStartedAt && task.timerStartedAt > 0);
  const hasTrackedTime = (task.trackedTime || 0) > 0;
  return hasRunningTimer || hasTrackedTime;
}

/**
 * Resolves the target status-mode Kanban column ID ('open' or 'todo', 'hold', 'completed')
 * for a task.
 */
export function resolveStatusKanbanBucket(
  task: Pick<Task, 'completed' | 'status'>,
  openColumnId: 'open' | 'todo' = 'open'
): 'open' | 'todo' | 'hold' | 'completed' {
  if (isTaskCompleted(task)) return 'completed';
  if (isTaskOnHold(task)) return 'hold';
  return openColumnId;
}

export const STANDARD_DATE_BUCKET_IDS = [
  'overdue',
  'today',
  'week',
  'month',
  'longterm',
  'hold',
  'completed',
] as const;

export type StandardDateBucketId = (typeof STANDARD_DATE_BUCKET_IDS)[number];

export interface StandardDateBucketDef {
  id: StandardDateBucketId;
  title: string;
  defaultColor: string;
}

export const STANDARD_DATE_BUCKET_DEFS: readonly StandardDateBucketDef[] = [
  { id: 'overdue', title: 'Überfällig', defaultColor: 'bg-rose-600' },
  { id: 'today', title: 'Heute', defaultColor: 'bg-indigo-600' },
  { id: 'week', title: 'Woche', defaultColor: 'bg-blue-500' },
  { id: 'month', title: 'Monat', defaultColor: 'bg-slate-500' },
  { id: 'longterm', title: 'Langfristig', defaultColor: 'bg-indigo-400' },
  { id: 'hold', title: 'Wartend', defaultColor: 'bg-amber-500' },
  { id: 'completed', title: 'Erledigt', defaultColor: 'bg-emerald-600' },
];
